import { useTranslation } from "react-i18next";
import {
  Drawer,
  Space,
  Button,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Grid,
  Divider,
  Select,
  App,
  Spin,
} from "antd";
import {
  useCreateWorkingHoursMutation,
  useGetAllMyStoresQuery,
  useUpdateWorkingHoursMutation,
} from "../graphql/generated/hooks";
import WeekDaysCheckBoxes from "./WeekDaysCheckBoxes";
import { CopyOutlined } from "@ant-design/icons";
import { useWorkingHoursForm } from "../hooks/useWorkingHoursForm";
import DayField from "./DayField";

const { useBreakpoint } = Grid;

type Props = {
  open: boolean;
  onClose: () => void;
  edit?: boolean;
  editData?: any;
  refetch?: () => void;
};

function MutateWorkingHoursDrawer({
  open,
  onClose,
  edit = false,
  editData,
  refetch,
}: Props) {
  const { t, i18n } = useTranslation();
  const { xl, md } = useBreakpoint();
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const {
    selectedWeekDays,
    setSelectedWeekDays,
    initialValues,
    itemsInitialValues,
    ready,
  } = useWorkingHoursForm(edit, editData);

  const [createWorkingHours] = useCreateWorkingHoursMutation();
  const [updateWorkingHours] = useUpdateWorkingHoursMutation();

  const { data: stores, loading: loadingStores } = useGetAllMyStoresQuery();

  const handleCopyToAll = () => {
    if (selectedWeekDays.length === 0) {
      message.warning(t("message.warning.no_days_selected"));
      return;
    }
    const firstDay = selectedWeekDays[0];
    const firstDayValues = form.getFieldValue(firstDay);
    if (!firstDayValues || firstDayValues.length === 0) {
      message.warning(t("message.warning.no_times_to_copy"));
      return;
    }
    const updatedValues = selectedWeekDays.reduce((acc, day) => {
      acc[day] = firstDayValues;
      return acc;
    }, {} as any);
    form.setFieldsValue(updatedValues);
    message.success(t("message.success.copied_to_all"));
  };

  const buildItems = (values: any, days: number[]) =>
    days.flatMap((day) =>
      values[day].map((item: any) => ({
        weekday: day,
        startTime: item.timeRange[0].format("HH:mm"),
        endTime: item.timeRange[1].format("HH:mm"),
      }))
    );

  const onFinish = (values: any) => {
    const vars = {
      name: values.name,
      nameAr: values.nameAr,
      startDate: values.period?.[0]?.format("YYYY-MM-DD"),
      endDate: values.period?.[1]?.format("YYYY-MM-DD"),
      priority: +values.priority,
      storeIds: values.stores,
      items: buildItems(values, selectedWeekDays),
    };
    if (edit)
      updateWorkingHours({ variables: { id: editData.id, ...vars } }).then(() =>
        refetch?.()
      );
    else createWorkingHours({ variables: vars }).then(() => refetch?.());
    onClose();
  };

  return (
    <Drawer
      placement={i18n.dir() === "rtl" ? "left" : "right"}
      destroyOnClose
      title={edit ? t("title.edit_wrokingHours") : t("title.add_wrokingHours")}
      width={xl ? "50%" : md ? "90%" : "100%"}
      onClose={onClose}
      open={open}
      loading={!ready}
      styles={{
        body: {
          paddingBottom: 80,
        },
      }}
      extra={
        <Space>
          <Button onClick={onClose}>{t("button.cancel")}</Button>
          <Button
            onClick={() => {
              form.submit();
            }}
            type="primary"
          >
            {t("button.save")}
          </Button>
        </Space>
      }
    >
      {!ready ? (
        <Spin />
      ) : (
        <Form
          // preserve={false}
          layout="vertical"
          form={form}
          onFinish={onFinish}
          initialValues={initialValues}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t("label.name")}
                rules={[
                  { required: true, message: t("message.error.name_required") },
                ]}
              >
                <Input placeholder={t("placeholder.name")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nameAr"
                label={t("label.name_ar")}
                rules={[
                  {
                    required: true,
                    message: t("message.error.name_ar_required"),
                  },
                ]}
              >
                <Input placeholder={t("placeholder.name_ar")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                tooltip={t("tooltip.priority")}
                name="priority"
                label={t("label.priority")}
                rules={[
                  {
                    required: true,
                    message: t("message.error.priority_required"),
                  },
                ]}
              >
                <Input placeholder={t("placeholder.priority")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                tooltip={t("tooltip.period")}
                name="period"
                label={t("label.period")}
                rules={[{ required: false }]}
              >
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  getPopupContainer={(trigger) => trigger.parentElement!}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="stores"
                label={t("label.applied_stores")}
                rules={[{ required: false }]}
              >
                <Select
                  loading={loadingStores}
                  placeholder={t("placeholder.select_stores")}
                  mode="multiple"
                  options={stores?.getAllStoresForPartner?.map((store) => ({
                    value: store.id,
                    label: store.name,
                  }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Row justify={"center"} style={{ marginBottom: 20 }}>
            <Col span={18}>
              <WeekDaysCheckBoxes
                initialValue={selectedWeekDays}
                onChange={(values) => {
                  setSelectedWeekDays(values);
                }}
              />
            </Col>
          </Row>
          <Row justify={"center"}>
            <Col span={24}>
              {selectedWeekDays.sort().map((day, dayIndex) => (
                <Row gutter={[10, 10]} style={{ marginBottom: 10 }} key={day}>
                  <Col span={3}>{t(`label.weekday_${day}`)}</Col>
                  <Col span={15}>
                    <DayField
                      day={day}
                      itemsInitialValues={itemsInitialValues}
                      form={form}
                    />
                  </Col>
                  <Col span={dayIndex !== 0 ? 0 : 6}>
                    <Button
                      shape="circle"
                      onClick={handleCopyToAll}
                      icon={<CopyOutlined />}
                    />
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        </Form>
      )}
    </Drawer>
  );
}

export default MutateWorkingHoursDrawer;
